/**
 * Timezone-correct scheduling helpers for the composer (no date library):
 * wall-clock time in an IANA zone <-> UTC instants, and "next occurrence" of a
 * weekly best-time slot. DST-safe via Intl.DateTimeFormat offsets.
 */

/** ISO weekday labels, index 1 = Monday … 7 = Sunday. */
export const WEEKDAY_SHORT = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function zonedParts(instant: Date, tz: string): ZonedParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(instant)) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 1,
  };
}

/** Offset (ms) of `tz` from UTC at `instant` (e.g. -6h for MDT). */
export function tzOffsetMs(instant: Date, tz: string): number {
  const p = zonedParts(instant, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const truncated = Math.floor(instant.getTime() / 60_000) * 60_000;
  return asUtc - truncated;
}

/** "2026-11-03" + "09:30" wall-clock in `tz` → the UTC instant. */
export function zonedWallTimeToUtc(date: string, time: string, tz: string): Date {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  // Two passes settle DST transitions (the offset at the guess vs at the result).
  let result = guess - tzOffsetMs(new Date(guess), tz);
  result = guess - tzOffsetMs(new Date(result), tz);
  return new Date(result);
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The wall-clock date/time of `instant` in `tz` as input-ready strings. */
export function wallTimeInZone(instant: Date, tz: string): { date: string; time: string; weekday: number } {
  const p = zonedParts(instant, tz);
  return {
    date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
    weekday: p.weekday,
  };
}

/**
 * Next future occurrence of a weekly (ISO weekday, hour) slot in `tz`, at
 * least `minLeadMinutes` from `now`.
 */
export function nextSlotOccurrence(
  weekday: number,
  hour: number,
  tz: string,
  now = new Date(),
  minLeadMinutes = 15
): Date {
  const earliest = now.getTime() + minLeadMinutes * 60_000;
  const today = wallTimeInZone(now, tz);
  const [y, m, d] = today.date.split("-").map(Number);
  for (let offset = 0; offset <= 14; offset++) {
    const day = new Date(Date.UTC(y, m - 1, d + offset));
    const isoDow = ((day.getUTCDay() + 6) % 7) + 1;
    if (isoDow !== weekday) continue;
    const candidate = zonedWallTimeToUtc(
      `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}`,
      `${pad(hour)}:00`,
      tz
    );
    if (candidate.getTime() >= earliest) return candidate;
  }
  // Unreachable for a valid weekday (a 7-day window always contains it twice).
  return new Date(earliest);
}

export function formatHour(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? "AM" : "PM"}`;
}

export function formatSlot(weekday: number, hour: number): string {
  return `${WEEKDAY_SHORT[weekday] ?? "?"} ${formatHour(hour)}`;
}

/** "Tue, Nov 3, 9:30 AM MST" for an instant shown in `tz`. */
export function formatInZone(instant: Date, tz: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(instant);
}

/** Whole-day difference between two `YYYY-MM-DD` wall-clock dates (b - a). */
function dateDiffDays(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/**
 * "Today 4:45 PM (MDT)" / "Tomorrow 9:00 AM (MDT)" / "Fri, Oct 9 4:45 PM
 * (MDT)" — a proposed/planned instant shown relative to "now", both read in
 * the same IANA zone (falls back to the browser's zone when `tz` is empty).
 */
export function formatRelativeDayTime(iso: string, tz?: string | null, now: Date = new Date()): string {
  const zone = tz && isValidTimezone(tz) ? tz : browserTimezone();
  const instant = new Date(iso);
  const nowParts = wallTimeInZone(now, zone);
  const instantParts = wallTimeInZone(instant, zone);
  const diff = dateDiffDays(nowParts.date, instantParts.date);
  const time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: zone }).format(instant);
  const tzLabel =
    new Intl.DateTimeFormat("en-US", { timeZoneName: "short", timeZone: zone })
      .formatToParts(instant)
      .find((p) => p.type === "timeZoneName")?.value ?? "";
  const dayLabel =
    diff === 0
      ? "Today"
      : diff === 1
        ? "Tomorrow"
        : new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: zone }).format(
            instant
          );
  return tzLabel ? `${dayLabel} ${time} (${tzLabel})` : `${dayLabel} ${time}`;
}
